import { Card, Empty, Skeleton, TabPane, Tabs, Typography } from "@douyinfe/semi-ui";
import { VChart } from "@visactor/react-vchart";
import { CSSProperties, useState } from "react";
import { CallEffectivenessDto, DailyTrendItem, DashboardFilters, HeatmapDto, HeatmapCell, HomeDashboardApi, SliceItem } from "../api/homeDashboard";
import { useDashboardModule } from "../hooks/useDashboardModule";
import { chartBoxStyle, chartTitleStyle, kpiBoxStyle, kpiItemStyle, PALETTE_GENERAL, PALETTE_STAR } from "../styles";
import { DrawerMode } from "./DetailDrawer";

interface Props {
  filters: DashboardFilters;
  isAdmin: boolean;
  currentTeamId?: number;
  openDrawer: (title: string, params: Record<string, any>, mode?: DrawerMode) => void;
}

export default function CustomerCallCard({ filters, openDrawer }: Props) {
  const [heatmapMode, setHeatmapMode] = useState<"week" | "month">("week");

  const eff = useDashboardModule(
    () => HomeDashboardApi.getCallEffectiveness(filters),
    [filters.startDate, filters.endDate, filters.teamId, JSON.stringify(filters.saleIds)],
    !!filters.startDate
  );
  const trend = useDashboardModule(
    () => HomeDashboardApi.getCallDailyTrend(filters),
    [filters.startDate, filters.endDate, filters.teamId, JSON.stringify(filters.saleIds)],
    !!filters.startDate && trendDays(filters) > 3
  );
  const newStar = useDashboardModule(
    () => HomeDashboardApi.getCallNewStarDist(filters),
    [filters.startDate, filters.endDate, filters.teamId, JSON.stringify(filters.saleIds)],
    !!filters.startDate
  );
  const heatmap = useDashboardModule(
    () => HomeDashboardApi.getCallHeatmap(heatmapMode, filters.teamId, filters.saleIds),
    [heatmapMode, filters.teamId, JSON.stringify(filters.saleIds)],
    true
  );

  const days = trendDays(filters);

  return (
    <Card>
      {eff.loading && <Skeleton.Title />}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <div style={chartBoxStyle}>
          <Typography.Text style={chartTitleStyle}>拨打有效占比</Typography.Text>
          {eff.loading && <Skeleton.Title />}
          {eff.data ? (
            <DonutEffectiveness dto={eff.data}
              onClick={(k) => openDrawer(`客户通话 - ${k}`, { ...filters, effectiveCall: k === "有效" ? 1 : 0 }, "call")} />
          ) : !eff.loading ? <Empty title="暂无数据" /> : null}
        </div>
        <div style={chartBoxStyle}>
          <Typography.Text style={chartTitleStyle}>总览 KPI</Typography.Text>
          {eff.loading && <Skeleton.Title />}
          {eff.data ? (
            <div style={kpiBoxStyle}>
              <Kpi label="总分配" value={eff.data.newAssigned + eff.data.oldAssigned} />
              <Kpi label="分配新客" value={eff.data.newAssigned} />
              <Kpi label="分配老客" value={eff.data.oldAssigned} />
              <Kpi label="总通话" value={eff.data.totalCalls} />
              <Kpi label="有效通话" value={eff.data.effectiveCalls} />
              <Kpi label="有效率" value={eff.data.effectiveRate.toFixed(1) + "%"} />
            </div>
          ) : !eff.loading ? <Empty title="暂无数据" /> : null}
        </div>
        <div style={chartBoxStyle}>
          <Typography.Text style={chartTitleStyle}>新分星级占比</Typography.Text>
          {newStar.loading && <Skeleton.Title />}
          {newStar.data && newStar.data.starDist.length > 0 ? (
            <SimplePie items={newStar.data.starDist}
              palette={PALETTE_STAR}
              onClick={(s) => openDrawer(`客户通话 - ${s.label}星`, { ...filters, newStar: s.key })} />
          ) : !newStar.loading ? <Empty title="暂无星级数据" /> : null}
        </div>
      </div>

      {days > 3 && (
        <div style={{ ...chartBoxStyle, flex: "1 1 100%", marginTop: 8 }}>
          <Typography.Text style={chartTitleStyle}>每日趋势（分组：分配 / 通话 / 有效）</Typography.Text>
          {trend.loading && <Skeleton.Title />}
          {trend.data && trend.data.length > 0 ? (
            <VChart spec={{
              type: "bar",
              data: [{ values: buildTrendFlat(trend.data) }],
              xField: ["date", "series"], yField: "value", seriesField: "series",
              color: { range: ["#0093FF", "#23C343", "#F7BA1E"] },
              barMaxWidth: 20,
              barGapInGroup: 4,
              bar: { style: { cornerRadius: [3, 3, 0, 0] } },
              label: { visible: true, position: "top", text: "{value}", overlap: false, style: { fontSize: 10 } },
              legends: [{ visible: true, position: "top" }],
            } as any} style={{ height: 220 }}
              onClick={(e: any) => {
                const date = e?.datum?.["date"];
                if (date) openDrawer(`客户通话 - 每日 ${date}`, { ...filters, day: date }, "call");
              }} />
          ) : !trend.loading ? <Empty title="所选区间暂无通话数据" /> : null}
        </div>
      )}

      <div style={{ ...chartBoxStyle, flex: "1 1 100%", marginTop: 8 }}>
        <Typography.Text style={chartTitleStyle}>通话热力图</Typography.Text>
        <Tabs onChange={(k) => setHeatmapMode(k as any)}>
          <TabPane tab="周" itemKey="week" />
          <TabPane tab="月" itemKey="month" />
        </Tabs>
        {heatmap.loading && <Skeleton.Title />}
        {heatmap.data && heatmap.data.cells.length > 0 ? (
          <HeatmapTableView dto={heatmap.data}
            onClick={(cell) => {
              if (dto_isMonth(heatmap.data!) && cell.date) {
                openDrawer(`客户通话 - ${cell.date} ${cell.hour}时`, {
                  ...filters,
                  startDate: cell.date,
                  endDate: cell.date,
                  effectiveCall: true,
                }, "call");
              } else {
                openDrawer(`客户通话 - ${cell.label}`, { ...filters, effectiveCall: true }, "call");
              }
            }} />
        ) : !heatmap.loading ? <Empty title="暂无热力数据" /> : null}
      </div>
    </Card>
  );
}

function trendDays(f: DashboardFilters): number {
  try {
    const ms = new Date(f.endDate).getTime() - new Date(f.startDate).getTime();
    return Math.ceil(ms / 86400000) + 1;
  } catch {
    return 0;
  }
}

function buildTrendFlat(items: DailyTrendItem[]): { date: string; series: string; value: number }[] {
  const out: { date: string; series: string; value: number }[] = [];
  items.forEach(d => {
    out.push({ date: d.date, series: "分配", value: d.newAssigned });
    out.push({ date: d.date, series: "通话", value: d.called });
    out.push({ date: d.date, series: "有效通话", value: d.effective });
  });
  return out;
}

function DonutEffectiveness({ dto, onClick }: { dto: CallEffectivenessDto; onClick: (k: string) => void }) {
  const data = [{ name: "有效", value: dto.effectiveCalls }, { name: "无效", value: dto.ineffectiveCalls }];
  return <VChart spec={{
    type: "pie", data: [{ values: data }], valueField: "value", categoryField: "name",
    color: { range: ["#23C343", "#86909C"] },
    pie: { center: ["50%", "50%"], radius: ["45%", "70%"] },
    label: { visible: true, position: "inside", text: "{value}" },
    legends: [{ visible: true, position: "right" }],
  } as any} style={{ height: 220 }}
    onClick={(e: any) => {
      const name = e?.datum?.["name"];
      if (name) onClick(name as string);
    }} />;
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  return <div style={kpiItemStyle}>
    <div style={{ fontSize: 12, color: "var(--semi-color-text-2)" }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
  </div>;
}

function SimplePie({ items, palette, onClick }: { items: SliceItem[]; palette?: string[]; onClick: (s: SliceItem) => void }) {
  return <VChart spec={{
    type: "pie", data: [{ values: items.map(i => ({ name: i.label, value: i.count })) }],
    valueField: "value", categoryField: "name",
    ...(palette ? { color: { range: palette } } : {}),
    label: { visible: true, position: "outside", text: "{name}: {value}" },
    legends: [{ visible: true, position: "right" }],
  } as any} style={{ height: 220 }}
    onClick={(e: any) => {
      const name = e?.datum?.["name"];
      const raw = items.find(i => i.label === name);
      if (raw) onClick(raw);
    }} />;
}

const thStyle: CSSProperties = {
  padding: "4px 6px", textAlign: "center",
  fontSize: 11, color: "var(--semi-color-text-2)",
  borderBottom: "1px solid var(--semi-color-border)",
  minWidth: 24,
};

const rowHeaderStyle: CSSProperties = {
  padding: "4px 6px", textAlign: "center",
  fontSize: 11, fontWeight: 600,
  color: "var(--semi-color-text-1)",
  borderRight: "1px solid var(--semi-color-border)",
  position: "sticky", left: 0,
  background: "var(--semi-color-bg-1)",
};

const cellStyle: CSSProperties = {
  padding: "4px 6px", textAlign: "center",
  fontSize: 11, minWidth: 24, height: 24,
  color: "var(--semi-color-text-1)",
};

function dto_isMonth(dto: HeatmapDto): boolean {
  return dto.mode === "month";
}

const WEEK_ROWS: { rowKey: string; label: string }[] = [
  { rowKey: "1", label: "周一" },
  { rowKey: "2", label: "周二" },
  { rowKey: "3", label: "周三" },
  { rowKey: "4", label: "周四" },
  { rowKey: "5", label: "周五" },
  { rowKey: "6", label: "周六" },
  { rowKey: "0", label: "周日" },
];

function HeatmapTableView({ dto, onClick }: { dto: HeatmapDto; onClick: (cell: any) => void }) {
  const max = Math.max(1, ...dto.cells.map(c => c.count));
  const rows: { rowKey: string; label: string }[] = dto.mode === "week"
    ? WEEK_ROWS
    : Array.from({ length: 31 }, (_, i) => ({ rowKey: String(i + 1), label: String(i + 1) }));
  // 仅展示工作时段 8:00-20:00，下班时段不展示
  const hours = Array.from({ length: 13 }, (_, i) => i + 8);
  const cellMap = new Map<string, HeatmapCell>();
  dto.cells.forEach(c => {
    let rowKey: string;
    if (dto.mode === "week") {
      rowKey = c.dow || "";
    } else {
      const parts = (c.date || "").split("-");
      rowKey = parts.length === 3 ? String(parseInt(parts[2], 10)) : "";
    }
    cellMap.set(`${rowKey}_${c.hour}`, c);
  });
  return (
    <div style={{ overflowX: "auto", maxHeight: 280 }}>
      <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
        <thead>
          <tr>
            <th style={thStyle}></th>
            {hours.map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.rowKey}>
              <td style={rowHeaderStyle}>{r.label}</td>
              {hours.map(h => {
                const cell = cellMap.get(`${r.rowKey}_${h}`);
                const count = cell?.count ?? 0;
                const alpha = count > 0 ? 0.15 + 0.85 * (count / max) : 0;
                return (
                  <td key={h}
                    style={{
                      ...cellStyle,
                      background: count > 0 ? `rgba(0, 122, 255, ${alpha})` : "transparent",
                      cursor: count > 0 ? "pointer" : "default",
                    }}
                    onClick={() => cell && onClick({
                      ...cell,
                      label: dto.mode === "week"
                        ? `${r.label} ${cell.hour}时`
                        : `${cell.date} ${cell.hour}时`,
                    })}>
                    {count > 0 ? count : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
